<template>
  <div class="py-2">
    <!-- Enable Toggle -->
    <div class="flex mt-3 items-center">
      <label class="text-left text-sm font-medium w-36 text-gray-700"
        >HIPAA Logging :</label
      >
      <div class="flex items-center">
        <input
          id="hipaa-audit-enabled"
          :defaultChecked="hipaaConfig.enabled"
          type="checkbox"
          class="mr-2 custom-checkbox"
          @change="update('enabled', $event.target.checked)"
        />
        <label
          for="hipaa-audit-enabled"
          class="text-xs text-left font-normal cursor-pointer"
          >Enable Audit Logging</label
        >
      </div>
    </div>

    <template v-if="hipaaConfig.enabled">
      <!-- Section: Fields to Capture -->
      <div class="mt-5">
        <div class="flex items-center mb-2">
          <label class="text-left text-sm font-medium w-36 text-gray-700"
            >Capture Fields :</label
          >
          <span class="text-xs text-gray-400"
            >Select what user data to include in each log entry</span
          >
        </div>
        <div class="ml-36 grid grid-cols-2 gap-2">
          <label
            v-for="field in availableFields"
            :key="field.key"
            class="flex items-center gap-2 text-xs text-gray-700 cursor-pointer"
          >
            <input
              type="checkbox"
              class="custom-checkbox"
              :checked="hipaaConfig.fields.includes(field.key)"
              @change="toggleField(field.key, $event.target.checked)"
            />
            <span>
              {{ field.label }}
              <span v-if="field.recommended" class="text-blue-400 ml-1"
                >(recommended)</span
              >
            </span>
          </label>
        </div>
      </div>

      <!-- Section: Events to Track (excludes button clicks — configured separately below) -->
      <div class="mt-5">
        <div class="flex items-center mb-2">
          <label class="text-left text-sm font-medium w-36 text-gray-700"
            >Track Events :</label
          >
          <span class="text-xs text-gray-400"
            >Select which user interactions to log</span
          >
        </div>
        <div class="ml-36 grid grid-cols-2 gap-2">
          <label
            v-for="event in availableEvents"
            :key="event.key"
            class="flex items-center gap-2 text-xs text-gray-700 cursor-pointer"
          >
            <input
              type="checkbox"
              class="custom-checkbox"
              :checked="hipaaConfig.events.includes(event.key)"
              @change="toggleEvent(event.key, $event.target.checked)"
            />
            <span>
              {{ event.label }}
              <span v-if="event.recommended" class="text-blue-400 ml-1"
                >(recommended)</span
              >
            </span>
          </label>
        </div>
      </div>

      <!-- Section: Button click logging (mutually exclusive modes) -->
      <div class="mt-5">
        <div class="flex items-start mb-2">
          <label class="text-left text-sm font-medium w-36 text-gray-700 pt-1"
            >Button Clicks :</label
          >
          <div class="flex-grow space-y-2">
            <label
              class="flex items-center gap-2 text-xs text-gray-700 cursor-pointer"
            >
              <input
                type="radio"
                name="button-log-mode"
                class="custom-checkbox"
                value="none"
                :checked="buttonLogMode === 'none'"
                @change="setButtonLogMode('none')"
              />
              <span>Don't log button clicks</span>
            </label>
            <label
              class="flex items-center gap-2 text-xs text-gray-700 cursor-pointer"
            >
              <input
                type="radio"
                name="button-log-mode"
                class="custom-checkbox"
                value="all"
                :checked="buttonLogMode === 'all'"
                @change="setButtonLogMode('all')"
              />
              <span>Log all button clicks</span>
            </label>
            <label
              class="flex items-start gap-2 text-xs text-gray-700 cursor-pointer"
            >
              <input
                type="radio"
                name="button-log-mode"
                class="custom-checkbox mt-0.5"
                value="single"
                :checked="buttonLogMode === 'single'"
                @change="setButtonLogMode('single')"
              />
              <span class="flex-grow">
                <span>Log one specific button only</span>
                <template v-if="buttonLogMode === 'single'">
                  <input
                    type="text"
                    class="block w-full max-w-md mt-2 px-3 py-2 border border-gray-300 custom-focus rounded-md h-8 text-sm"
                    placeholder="CSS class on the button, e.g. hipaa-log-btn"
                    :value="hipaaConfig.tracked_button_class || ''"
                    @input="updateTrackedButtonClass($event.target.value)"
                  />
                  <p class="text-xs text-gray-400 mt-1">
                    Add this exact class to the button in your site component.
                  </p>
                </template>
              </span>
            </label>
          </div>
        </div>
      </div>

      <!-- Info Banner -->
      <div
        class="ml-36 mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md text-xs text-blue-700"
      >
        <font-awesome-icon icon="info-circle" class="mr-1" />
        Logs are sent from the viewer in real-time. The actual storage happens
        on the backend. Sensitive PHI (field values) is
        <strong>never</strong> logged — only actions and metadata.
      </div>
    </template>
  </div>
</template>

<script>
const DEFAULT_CONFIG = {
  enabled: false,
  fields: ["email", "name", "ip", "action", "timestamp"],
  events: ["page_view", "button_click", "form_submit", "login", "logout"],
  button_log_mode: "all",
  tracked_button_class: "",
};

export default {
  name: "SettingsHipaaAudit",
  emits: ["update"],
  props: {
    hipaaConfig: {
      type: Object,
      default: () => ({ ...DEFAULT_CONFIG }),
    },
  },
  data() {
    return {
      availableFields: [
        { key: "email", label: "User Email", recommended: true },
        { key: "name", label: "User Name", recommended: true },
        { key: "ip", label: "IP Address", recommended: true },
        { key: "action", label: "Action Performed", recommended: true },
        { key: "timestamp", label: "Timestamp", recommended: true },
        { key: "session_id", label: "Session ID", recommended: true },
        { key: "page", label: "Page / URL", recommended: false },
        { key: "device", label: "Device / Browser Info", recommended: false },
        { key: "element", label: "Element Interacted", recommended: false },
        {
          key: "form_name",
          label: "Form Name (not values)",
          recommended: false,
        },
      ],
      availableEvents: [
        { key: "page_view", label: "Page View", recommended: true },
        { key: "form_submit", label: "Form Submit", recommended: true },
        { key: "login", label: "Login", recommended: true },
        { key: "logout", label: "Logout", recommended: true },
        { key: "file_download", label: "File Download", recommended: true },
        { key: "link_click", label: "Link Click", recommended: false },
        { key: "input_change", label: "Input Change", recommended: false },
      ],
    };
  },
  computed: {
    buttonLogMode() {
      if (this.hipaaConfig.button_log_mode) {
        return this.hipaaConfig.button_log_mode;
      }
      // Legacy sites: infer mode from stored values
      const events = this.hipaaConfig.events || [];
      if (!events.includes("button_click")) return "none";
      if (this.hipaaConfig.tracked_button_class) return "single";
      return "all";
    },
  },
  methods: {
    update(key, value) {
      this.$emit("update", {
        hipaaConfig: {
          ...this.hipaaConfig,
          [key]: value,
        },
      });
    },
    syncButtonClickEvent(events, enabled) {
      const next = [...events];
      const idx = next.indexOf("button_click");
      if (enabled && idx === -1) next.push("button_click");
      if (!enabled && idx !== -1) next.splice(idx, 1);
      return next;
    },
    setButtonLogMode(mode) {
      const events = this.hipaaConfig.events || [];
      if (mode === "none") {
        this.$emit("update", {
          hipaaConfig: {
            ...this.hipaaConfig,
            button_log_mode: "none",
            tracked_button_class: "",
            events: this.syncButtonClickEvent(events, false),
          },
        });
        return;
      }
      if (mode === "all") {
        this.$emit("update", {
          hipaaConfig: {
            ...this.hipaaConfig,
            button_log_mode: "all",
            tracked_button_class: "",
            events: this.syncButtonClickEvent(events, true),
          },
        });
        return;
      }
      this.$emit("update", {
        hipaaConfig: {
          ...this.hipaaConfig,
          button_log_mode: "single",
          events: this.syncButtonClickEvent(events, true),
        },
      });
    },
    toggleField(fieldKey, checked) {
      const fields = [...(this.hipaaConfig.fields || [])];
      if (checked && !fields.includes(fieldKey)) {
        fields.push(fieldKey);
      } else if (!checked) {
        const idx = fields.indexOf(fieldKey);
        if (idx !== -1) fields.splice(idx, 1);
      }
      this.$emit("update", {
        hipaaConfig: { ...this.hipaaConfig, fields },
      });
    },
    toggleEvent(eventKey, checked) {
      const events = [...(this.hipaaConfig.events || [])];
      if (checked && !events.includes(eventKey)) {
        events.push(eventKey);
      } else if (!checked) {
        const idx = events.indexOf(eventKey);
        if (idx !== -1) events.splice(idx, 1);
      }
      this.$emit("update", {
        hipaaConfig: { ...this.hipaaConfig, events },
      });
    },
    sanitizeClassToken(value) {
      return String(value || "")
        .trim()
        .replace(/^\./, "")
        .replace(/\s+/g, "");
    },
    updateTrackedButtonClass(value) {
      this.$emit("update", {
        hipaaConfig: {
          ...this.hipaaConfig,
          button_log_mode: "single",
          tracked_button_class: this.sanitizeClassToken(value),
          events: this.syncButtonClickEvent(
            this.hipaaConfig.events || [],
            true,
          ),
        },
      });
    },
  },
};
</script>
